/* eslint-disable jsx-a11y/no-redundant-roles -- role='list' / 'listitem' are the design's: Safari drops list semantics from a list styled with list-style: none */
import { Trans, useTranslation } from 'next-i18next';
import { memo, useEffect, useRef } from 'react';

import { TrialCta } from '../components/trial-cta';
import { CONTAINER_CLASS, EYEBROW_CLASS, KICKER_CLASS, SECTION_CLASS, SectionBackground } from '../components/ui';
import { FINAL_FORM_ANCHOR, V4A_NAMESPACE } from '../constants';
import { createCityNamer, createCountryNamer, loadGeoData, localeTag } from '../engines/geo';
import { startMulticonexao } from '../engines/multiconexao-engine';

interface StaticRoute {
  id: string;
  code: string;
  city: string;
  country: 'us' | 'br' | 'cl' | 'ar';
  ms: number;
  delta: number | null;
  spark: string;
}

/* The card's first paint (and its no-script state), exactly the source's: the engine swaps these rows
   for the visitor's own routes as soon as it starts. */
const STATIC_ROUTES: readonly StaticRoute[] = [
  { id: 'r1', code: 'MIA', city: 'Miami', country: 'us', ms: 95, delta: 53, spark: '1.5,1.5 8.5,5 15.5,2.3 22.5,1.5 29.5,4.1 36.5,5.9 43.5,2.3 50.5,3.2' },
  { id: 'r2', code: 'RIO', city: 'Rio de Janeiro', country: 'br', ms: 52, delta: 10, spark: '1.5,10.3 8.5,13 15.5,9.4 22.5,11.2 29.5,12.1 36.5,8.5 43.5,10.3 50.5,11.2' },
  { id: 'r3', code: 'SAO', city: 'São Paulo', country: 'br', ms: 42, delta: null, spark: '1.5,11.9 8.5,12.8 15.5,11 22.5,12.8 29.5,11.9 36.5,13.7 43.5,12.8 50.5,13.7' },
  { id: 'r4', code: 'SCL', city: 'Santiago', country: 'cl', ms: 77, delta: 35, spark: '1.5,5.2 8.5,7.9 15.5,4.3 22.5,7 29.5,5.2 36.5,6.1 43.5,8.8 50.5,6.1' },
  { id: 'r5', code: 'BUE', city: 'Buenos Aires', country: 'ar', ms: 68, delta: 26, spark: '1.5,8.8 8.5,6.1 15.5,9.7 22.5,7 29.5,7.9 36.5,9.7 43.5,6.1 50.5,7.9' },
];

function RouteRow({ route }: { route: StaticRoute }) {
  const { t } = useTranslation(V4A_NAMESPACE);
  const country = t(`mc.static_country.${route.country}`);
  const lastPoint = route.spark.split(' ').pop()?.split(',') ?? ['50.5', '9'];
  const label = route.delta === null ? t('mc.route_label_winner', { city: route.city, country, ms: route.ms }) : t('mc.route_label', { city: route.city, country, ms: route.ms, delta: route.delta });
  return (
    <li role='listitem'>
      <button className={route.delta === null ? 'mc-route is-winner' : 'mc-route'} type='button' data-route={route.id} aria-pressed='false' aria-label={label}>
        <span className='mc-route-cc' aria-hidden='true'>
          {route.code}
        </span>
        <span className='mc-route-body'>
          <span className='mc-route-city'>{route.city}</span>
          <span className='mc-route-meta'>
            <span className='mc-route-country'>{country}</span>
            <span className='mc-route-tag'>{t('mc.fastest')}</span>
            <span className='mc-route-pin'>
              <svg viewBox='0 0 16 16' aria-hidden='true'>
                <path d='M9.6 1.8 14.2 6.4l-2 .5-1.8 1.8.4 3.4-1.3 1.3-2.9-2.9-4.2 4.2m3-5.5L2.6 6.4l1.3-1.3 3.4.4 1.8-1.8z' />
              </svg>
              {t('mc.pinned')}
            </span>
          </span>
        </span>
        <svg className='mc-route-spark' viewBox='0 0 52 18' aria-hidden='true' focusable='false'>
          <polygon className='sp-fill' points={`1.5,18 ${route.spark} 50.5,18`} />
          <polyline points={route.spark} />
          <circle className='sp-dot' r='1.75' cx={lastPoint[0]} cy={lastPoint[1]} />
        </svg>
        <span className='mc-route-lat'>
          <span className='mc-route-ms'>
            <span data-ms=''>{route.ms}</span>
            <span className='u'>ms</span>
          </span>
          <span className='mc-route-delta' data-delta=''>
            {route.delta === null ? t('mc.active_now') : t('mc.delta_ms', { ms: route.delta })}
          </span>
        </span>
      </button>
    </li>
  );
}

const PC_ICON = (
  <svg viewBox='0 0 40 40'>
    <rect x='5' y='7' width='30' height='20' rx='3' />
    <path d='M14 34h12M20 27v7' />
    <circle cx='20' cy='17' r='5' />
    <path d='M20 9v3.5M20 21.5V25M12 17h3.5M24.5 17H28' />
    <circle className='f' cx='20' cy='17' r='1.4' />
  </svg>
);

const GAME_ICON = (
  <svg viewBox='0 0 40 40'>
    <rect x='7' y='7' width='26' height='10' rx='2.5' />
    <rect x='7' y='23' width='26' height='10' rx='2.5' />
    <path d='M12 12h6M12 28h6' />
    <circle className='f' cx='27' cy='12' r='1.6' />
    <circle className='f' cx='27' cy='28' r='1.6' />
  </svg>
);

function MulticonexaoCardMarkup() {
  const { t } = useTranslation(V4A_NAMESPACE);
  return (
    <section className='mc' aria-labelledby='mc-title' data-mc=''>
      <div className='mc-card'>
        <header className='mc-head'>
          <span className='eyebrow'>{t('mc.eyebrow')}</span>
          <h2 className='mc-title' id='mc-title'>
            <Trans t={t} i18nKey='mc.title' components={{ a: <span className='mc-title-a' />, b: <span className='mc-title-b' />, em: <em /> }} />
          </h2>
          <p className='mc-lead'>
            <Trans t={t} i18nKey='mc.lead' components={{ b: <b /> }} />
          </p>
          <ul className='mc-facts' role='list'>
            <li>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M14 3 5 13.5h6.5L10 21l9-10.5h-6.5z' />
              </svg>
              <span>
                <Trans t={t} i18nKey='mc.fact_failover' components={{ b: <b /> }} />
              </span>
            </li>
            <li>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <circle cx='12' cy='12' r='9' />
                <path d='M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' />
              </svg>
              <span>
                <Trans t={t} i18nKey='mc.fact_network' components={{ b: <b /> }} />
              </span>
            </li>
          </ul>
        </header>

        <div className='mc-proof' role='group' aria-label={t('mc.proof_label')} data-proof=''>
          <p className='sr-only' data-proof-sr=''>
            {t('mc.proof_sr', { bad: 208, good: 42, pct: 80 })}
          </p>
          <div className='mc-proof-rows' aria-hidden='true'>
            <div className='mc-proof-item mc-proof-item--bad'>
              <span className='mc-proof-line'>
                <small>{t('mc.without')}</small>
                <strong>
                  <span data-proof-bad=''>208</span>
                  <span className='u'>ms</span>
                </strong>
              </span>
              <span className='mc-proof-bar'>
                <span className='mc-proof-fill' />
              </span>
            </div>
            <div className='mc-proof-item mc-proof-item--good'>
              <span className='mc-proof-line'>
                <small>{t('mc.with')}</small>
                <span className='mc-proof-pct'>
                  −<span data-proof-pct=''>80</span>%
                </span>
                <strong>
                  <span data-proof-good=''>42</span>
                  <span className='u'>ms</span>
                </strong>
              </span>
              <span className='mc-proof-bar'>
                <span className='mc-proof-fill [--mc-fill:20.2%]' data-proof-fill='' />
              </span>
            </div>
          </div>
          <p className='mc-proof-note'>
            <Trans t={t} i18nKey='mc.proof_note' components={{ b: <b /> }} />
          </p>
        </div>

        <p className='sr-only' id='mc-desc' data-mc-desc=''>
          {t('mc.description', { routes: 'Miami, Rio de Janeiro, São Paulo, Santiago, Buenos Aires', hub: 'São Paulo' })}
        </p>
        <div className='mc-stage' data-stage='' role='group' aria-label={t('mc.stage_label')} aria-describedby='mc-desc'>
          <svg className='mc-defs' aria-hidden='true' focusable='false'>
            <defs>
              <linearGradient id='mc-spg' x1='0' y1='0' x2='0' y2='1'>
                <stop className='g0' offset='0' />
                <stop className='g1' offset='1' />
              </linearGradient>
            </defs>
          </svg>
          <svg className='mc-wires' data-wires='' aria-hidden='true' focusable='false' />
          <div className='mc-packets' data-packets='' aria-hidden='true' />
          <div className='mc-node mc-node--pc' data-node='pc'>
            <div className='mc-node-icon' aria-hidden='true'>
              {PC_ICON}
            </div>
            <div className='mc-node-label'>
              <strong>{t('mc.pc')}</strong>
              <small data-pc-place=''>{t('mc.pc_place')}</small>
            </div>
          </div>
          <div className='mc-routes-wrap'>
            <div className='mc-key'>
              <span className='mc-key-col' aria-hidden='true'>
                <span className='x-long'>
                  <Trans t={t} i18nKey='mc.key_long' components={{ b: <b /> }} />
                </span>
                <span className='x-short'>
                  <b>{t('mc.key_short')}</b>
                </span>
              </span>
              <p className='mc-hint' data-hint='' aria-hidden='true'>
                <svg className='x-touch' viewBox='0 0 24 24'>
                  <circle className='dot' cx='15' cy='15' r='2.4' />
                  <path d='M9.17 17.12A6.2 6.2 0 0 1 17.12 9.17M5.04 18.63A10.6 10.6 0 0 1 18.63 5.04' />
                </svg>
                <svg className='x-hover' viewBox='0 0 24 24'>
                  <path d='M5 3l14 8.2-6.4 1.6L9.4 19z' />
                </svg>
                <span className='x-touch'>{t('mc.hint_touch')}</span>
                <span className='x-hover'>{t('mc.hint_hover')}</span>
              </p>
              <button className='mc-clear' type='button' data-clear='' hidden title={t('mc.clear_title')} aria-label={t('mc.clear_label')}>
                <svg viewBox='0 0 12 12' aria-hidden='true'>
                  <path d='M2.5 2.5l7 7M9.5 2.5l-7 7' />
                </svg>
                <span className='x-long'>{t('mc.clear_label')}</span>
                <span className='x-short'>{t('mc.clear_short')}</span>
              </button>
            </div>

            <ul className='mc-routes' role='list' data-routes='' aria-label={t('mc.routes_label')}>
              {STATIC_ROUTES.map((route) => (
                <RouteRow key={route.id} route={route} />
              ))}
            </ul>
            <p className='mc-direct-key' aria-hidden='true'>
              {t('mc.compare')}
            </p>
            <div className='mc-direct' data-direct='' role='group' aria-label={t('mc.direct_label', { ms: 166 })}>
              <i aria-hidden='true' />
              <span>
                <b>{t('mc.without')}</b>
                <span className='x-d'>
                  <span className='x-sep' />
                  {t('mc.direct_route')}
                </span>
              </span>
              <strong>
                +<span data-direct-ms=''>166</span>
                <span className='u'>ms</span>
              </strong>
            </div>
          </div>
          <div className='mc-node mc-node--game' data-node='game'>
            <div className='mc-node-icon' aria-hidden='true'>
              {GAME_ICON}
            </div>
            <div className='mc-node-label'>
              <strong>{t('mc.game')}</strong>
              <small data-game-place=''>{t('mc.game_place')}</small>
            </div>
          </div>

          <div className='mc-result' aria-hidden='true' data-result=''>
            <span className='mc-result-side mc-result-side--bad'>
              <small>{t('mc.without')}</small>
              <b>
                <span data-result-bad=''>208</span>
                <i>ms</i>
              </b>
            </span>
            <span className='mc-result-line'>
              <svg viewBox='0 0 12 12'>
                <path d='M3.5 1.5L8 6l-4.5 4.5' />
              </svg>
            </span>
            <span className='mc-result-side mc-result-side--good'>
              <small>{t('mc.with')}</small>
              <span className='mc-result-fig'>
                <span className='mc-proof-pct'>
                  −<span data-result-pct=''>80</span>%
                </span>
                <b>
                  <span data-result-good=''>42</span>
                  <i>ms</i>
                </b>
              </span>
            </span>
          </div>
        </div>

        <footer className='mc-foot'>
          <div className='mc-foot-text'>
            <p className='mc-status' data-status=''>
              <i aria-hidden='true' />
              <span className='mc-status-body' data-status-text='' dangerouslySetInnerHTML={{ __html: t('mc.status.initial', { best: '<b>São Paulo</b>', ms: 42, interpolation: { escapeValue: false } }) }} />
            </p>
            <div className='mc-foot-slot'>
              <p className='mc-bridge' data-bridge=''>
                {t('mc.bridge')}
              </p>
            </div>
          </div>
          <div className='mc-actions'>
            <button className='mc-pause' type='button' data-pause='' aria-label={t('mc.pause_label')} aria-pressed='false' title={t('mc.pause_title')}>
              <svg className='ic-pause' viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M8.5 5.5v13M15.5 5.5v13' />
              </svg>
              <svg className='ic-play' viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
              </svg>
            </button>
            <button className='btn btn-secondary mc-btn' type='button' data-retest='' aria-busy='false'>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7' />
              </svg>
              <span className='mc-btn-lbl'>
                <span data-lbl=''>{t('mc.retest')}</span>
                <span className='mc-btn-ghost' aria-hidden='true'>
                  {t('mc.retest')}
                </span>
                <span className='mc-btn-ghost' aria-hidden='true'>
                  {t('mc.retest_busy')}
                </span>
              </span>
            </button>
            <div className='mc-offer'>
              <TrialCta className='btn btn-primary mc-cta' anchor={FINAL_FORM_ANCHOR} data-cta=''>
                <span>{t('cta.try_free')}</span>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M5 12h13M13 6l6 6-6 6' />
                </svg>
              </TrialCta>
              <p className='mc-offer-note'>
                <Trans t={t} i18nKey='mc.offer_note' components={{ b: <b /> }} />
              </p>
            </div>
          </div>
          <p className='sr-only' role='status' aria-live='polite' data-live='' />
        </footer>
      </div>
    </section>
  );
}

/**
 * Memoised with no props: once the engine owns the live nodes, React never renders this subtree again
 * (a locale change remounts the whole section instead, the page keys it by locale).
 */
const MulticonexaoCard = memo(MulticonexaoCardMarkup);

/** "Aula I · Encantamento de rotas": the Multiconexão card, native (it was an iframe in the source). */
export function Multiconexao() {
  const { t, i18n } = useTranslation(V4A_NAMESPACE);
  const holdRef = useRef<HTMLDivElement>(null);
  const locale = i18n.language;

  useEffect(() => {
    const root = holdRef.current?.querySelector<HTMLElement>('[data-mc]');
    if (!root) return undefined;
    let stop: (() => void) | null = null;
    let cancelled = false;
    const listFormat = typeof Intl.ListFormat === 'function' ? new Intl.ListFormat(localeTag(locale), { style: 'long', type: 'conjunction' }) : null;
    loadGeoData()
      .then((geo) => {
        if (cancelled) return;
        stop = startMulticonexao(root, {
          geo,
          translate: (key, values) => String(i18n.t(key, { ns: V4A_NAMESPACE, ...values })),
          cityName: createCityNamer(geo, locale),
          countryName: createCountryNamer(geo, locale),
          joinList: (items) => (listFormat ? listFormat.format(items) : items.join(', ')),
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [i18n, locale]);

  return (
    <section className={`${SECTION_CLASS} sec--frame`} id='chapeu' aria-labelledby='mc-kicker'>
      <SectionBackground halos={[{ position: 'left' }]} />
      <div className={CONTAINER_CLASS}>
        <p className={EYEBROW_CLASS} data-reveal=''>
          {t('mc.section_eyebrow')}
        </p>
        <h2 className={KICKER_CLASS} id='mc-kicker' data-reveal=''>
          <Trans t={t} i18nKey='mc.kicker' components={{ b: <b className='font-bold text-[color:var(--fg-strong)]' /> }} />
        </h2>
      </div>
      <div ref={holdRef} className={`${CONTAINER_CLASS} frame-hold mc-embed`} id='mc-hold'>
        <MulticonexaoCard />
      </div>
    </section>
  );
}
